/**
 * PassengerNotice.js
 *
 * The message under the boarding pass, depending on who's looking at it:
 * a sign-in prompt, a form for the visitor's full name, or why the pass is hidden.
 */

import React, { useState } from "react";
import { Lock, LogIn, UserRound } from "lucide-react";
import { useClientAuth } from "../../../hooks/clientAuth";
import { saveFullName } from "../helpers/flyApi";

/**
 * A card with an icon, a title and some text
 */
const Notice = ({ accent, icon: Icon, title, children }) => (
    <div
        className="w-full flex gap-3 sm:gap-4 rounded-2xl px-4 py-4 sm:px-5 text-left"
        style={{ background: "#FBF7EF", color: "#1B2A41", borderLeft: `6px solid ${accent}`, boxShadow: "0 6px 18px rgba(27,42,65,0.10)" }}
    >
        <Icon className="shrink-0 mt-0.5" size={22} color={accent} aria-hidden="true" />
        <div className="flex flex-col gap-2">
            <p className="font-bold tracking-wide">{title}</p>
            {children}
        </div>
    </div>
);

const buttonClass = "self-start inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold tracking-[0.12em] text-[#FBF7EF] transition hover:opacity-90 disabled:opacity-50";

/**
 * Names compare like the backend does: ignoring case and extra spaces
 */
const sameName = (a, b) => {
    const tidy = (name) => name.normalize("NFKC").trim().split(/\s+/).join(" ").toLowerCase();
    return tidy(a) === tidy(b);
};

/**
 * Asks for the visitor's full name and saves it to their profile (once).
 * Starts with the name on their account (e.g. from Google), for them to check.
 * A name that doesn't match the ticket is never saved, since a saved name can't be changed and would lock them out.
 * @param {String} passengerName    -   The name on this ticket
 * @param {Function} onSaved        -   Called after saving, to load the pass again
 */
const FullNameForm = ({ accent, passengerName, onSaved }) => {
    const { user } = useClientAuth();
    const [name, setName] = useState(user?.displayName || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!sameName(name, passengerName)) {
            setError("That doesn't match the name on this ticket. Check the spelling, or message Yash if it's yours.");
            return;
        }
        setError("");
        setSaving(true);
        try {
            await saveFullName(name);
            onSaved();
        } catch (err) {
            // 409: already saved (e.g. in another tab), so just load the pass again
            if (err.status === 409) onSaved();
            else setError(err.message || "Couldn't save your name. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <p className="text-sm" style={{ color: "#5B6577" }}>
                Enter your full name exactly as it's on your invite. It's how we match you to your ticket,
                and it can't be changed later.
            </p>
            <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                maxLength={100}
                required
                className="w-full max-w-sm rounded-lg border px-3 py-2 bg-white focus:outline-none focus:ring-2"
                style={{ borderColor: "#C9C2B4", "--tw-ring-color": accent }}
            />
            {error && <p className="text-sm" style={{ color: "#A8183D" }}>{error}</p>}
            <button type="submit" disabled={saving || !name.trim()} className={buttonClass} style={{ background: accent }}>
                {saving ? "CHECKING…" : "CHECK MY TICKET"}
            </button>
        </form>
    );
};

/**
 * @param {Object} booking      -   { status, yourName, pass } from the backend
 * @param {Function} onSignIn   -   Opens the sign-in modal
 * @param {Function} onReload   -   Loads the pass again
 * @returns {JSX.Element|null}
 */
const PassengerNotice = ({ booking, onSignIn, onReload }) => {
    const accent = booking.pass.colour;

    switch (booking.status) {
        case "signed_out":
            return (
                <Notice accent={accent} icon={LogIn} title="Sign in to confirm your ticket">
                    <p className="text-sm" style={{ color: "#5B6577" }}>
                        Sign in and tell us you're coming. Then you'll see your seat and where you're flying from (only you can),
                        and you can check in online.
                    </p>
                    <button type="button" onClick={onSignIn} className={buttonClass} style={{ background: accent }}>
                        <LogIn size={16} aria-hidden="true" /> SIGN IN
                    </button>
                </Notice>
            );
        case "needs_name":
            return (
                <Notice accent={accent} icon={UserRound} title="Is this your boarding pass?">
                    <FullNameForm accent={accent} passengerName={booking.pass.fullName} onSaved={onReload} />
                </Notice>
            );
        case "name_mismatch":
            return (
                <Notice accent={accent} icon={Lock} title="This ticket is booked under a different name">
                    <p className="text-sm" style={{ color: "#5B6577" }}>
                        The name on your account{booking.yourName ? ` (${booking.yourName})` : ""} doesn't match the passenger on this ticket.
                        If this is your pass, message Yash to sort it out.
                    </p>
                </Notice>
            );
        case "other_passenger":
            return <Notice accent={accent} icon={Lock} title="This boarding pass belongs to another passenger." />;
        case "has_other_pass":
            return (
                <Notice accent={accent} icon={Lock} title="You already have a boarding pass">
                    <p className="text-sm" style={{ color: "#5B6577" }}>Your account is already linked to a different ticket on this flight.</p>
                </Notice>
            );
        default:
            return null;
    }
};

export default PassengerNotice;
